import React, { useEffect, useState } from "react";
import { Clock, Info } from "lucide-react";
import { timePresets } from "./constants";
import { useAddWorkingHour, useWorkingist } from "../../hooks";
import { useShared } from "@shared/context/SharedContext";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface WorkingHour {
  name: string;
  endTime: string;
  isActive: boolean;
  createdBy: string;
  startTime: string;
  createdDate: string;
  code: string;
}

const WorkingDaysSchedule = () => {
  const [workingHours, setWorkingHours] = useState<WorkingHour[]>([]);
  const [selectedDay, setSelectedDay] = useState("");
  const addWorkingHour = useAddWorkingHour();
  const utilityID = useRemoteUtilityId();
  const { data, isLoading, isError } = useWorkingist();
  const { setValue } = useShared();



  useEffect(() => {
  const defaultStart = "09:00:00";
  const defaultEnd = "17:00:00";
  const allDaysOfWeek = [
    "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
  ];

  if (data?.result) {
    const dayMap = new Map(
      data.result.map((item: any) => [item.name.toLowerCase(), item])
    );

    const fullWeek = allDaysOfWeek.map((dayName) => {
      const apiDay = dayMap.get(dayName.toLowerCase());
      return {
        name: dayName,
        code: apiDay?.name || dayName,
        isActive: apiDay?.isActive ?? false,
        startTime: apiDay?.startTime || defaultStart,
        endTime: apiDay?.endTime || defaultEnd,
        createdBy: apiDay?.createdBy || "",
        createdDate: apiDay?.createdDate || "",
      };
    });

    setWorkingHours(fullWeek);
  }
}, [data]);


  const toggleDay = (dayName: string) => {
    const updated = workingHours.map((day) =>
      day.name === dayName ? { ...day, isActive: !day.isActive } : day
    );
    setWorkingHours(updated);
  };

  const applyPresetToAll = (preset: { start: string; end: string }) => {
    const updated = workingHours.map((day) =>
      day.isActive
        ? { ...day, startTime: preset.start, endTime: preset.end }
        : day
    );
    setWorkingHours(updated);

    const payload = {
      remote_utility_id: utilityID,
      working_hours: updated
        .filter((day) => day.isActive)
        .map((day) => ({
          name: day.name,
          code: day.name,
          start_time: day.startTime,
          end_time: day.endTime,
        })),
    };

    console.log("Payload to send:", payload);
    addWorkingHour.mutate(payload);
  };

  const handleDaySelect = (day: WorkingHour) => {
    console.log("Selected Day:", day);
    setValue("selectedDayObject", day);
    setSelectedDay(day.name);
  };

  const renderDayRow = (day: WorkingHour) => (
    <div
      key={day.name}
      onClick={() => handleDaySelect(day)}
      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
        selectedDay === day.name
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={day.isActive}
          onChange={(e) => {
            e.stopPropagation();
            toggleDay(day.name);
          }}
          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
        />
        <span className="font-medium text-gray-900 capitalize">{day.name}</span>
      </div>
      <span className="text-sm text-gray-600">
        {day.isActive ? `${day.startTime} - ${day.endTime}` : "Non-working"}
      </span>
    </div>
  );

  if (isLoading) return <p>Loading...</p>;
  if (isError)
    return <p className="text-red-500">Failed to load data.</p>;

return (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <div className="flex flex-col gap-2 mb-4">
      <div  className="flex items-center ">
      <Clock className="w-5 h-5 text-gray-600" />
      <h3 className="card-heading-text pl-2">Working Days Schedule</h3>
      </div>
      <p className="text-xs font-small text-gray-700">Set up standard working hours and manage exceptions for each day</p>
    </div>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div className="flex items-center gap-2 text-blue-800">
        <Info className="w-4 h-4" />
        <span className="text-sm">
          Changes affect customer service and field operations scheduling.
        </span>
      </div>
    </div>

    <div className="flex flex-col lg:flex-row gap-6">
      {/* Weekdays */}
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Weekdays</h4>
        <div className="space-y-3">
          {workingHours
            .filter((day) => day.name !== "Saturday" && day.name !== "Sunday")
            .map((day) => (
              <div
                key={day.name}
                onClick={() => handleDaySelect(day)}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedDay === day.name
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={day.isActive}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleDay(day.name);
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm capitalize">
                    {day.name}
                  </span>
                </div>
                {day.isActive ? (
                  <span className="bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full">
                    {day.startTime} - {day.endTime}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500 italic">Non-working</span>
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Weekend + Presets */}
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Weekend</h4>
        <div className="space-y-3 mb-6">
          {workingHours
            .filter((day) => day.name === "Saturday" || day.name === "Sunday")
            .map((day) => (
              <div
                key={day.name}
                onClick={() => handleDaySelect(day)}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedDay === day.name
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={day.isActive}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleDay(day.name);
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm capitalize">
                    {day.name}
                  </span>
                </div>
                {day.isActive ? (
                  <span className="bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full">
                    {day.startTime} - {day.endTime}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500 italic">Non-working</span>
                )}
              </div>
            ))}
        </div>

        <div className="rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">
            Quick Time Presets
          </h4>
          <div className="flex flex-wrap gap-2">
            {timePresets.map((preset, index) => (
              <button
                key={index}
                onClick={() => applyPresetToAll(preset)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);


};

export default WorkingDaysSchedule;
